type MappedValue = {
  readonly [key in keyof ReturnType<typeof getDetailsRoute>]: `${ReturnType<typeof getDetailsRoute>[key]}/edit`;
};

const getCreateRoute = (root: string) => {
  const step = ':currentStep';
  const id = ':id?';
  const stepFull = `${step}/${id}`;

  return {
    step,
    stepFull,
    full: `${root}/create/${stepFull}`,
    partial: `${root}/create`,
    root: 'create',
  } as const;
};
const getDetailsRoute = (root: string) => {
  const tab = ':tab';
  return {
    tab,
    full: `${root}/:id/${tab}`,
    partial: `${root}/:id`,
    root: ':id',
  } as const;
};

const getMainPageTabsRoute = (root: string) =>
  ({
    tab: ':currentTab?',
    full: `${root}/:currentTab`,
  }) as const;

type EditMappedValue = MappedValue & {
  suffix: 'edit';
};
const getEditRoute = (root: string): EditMappedValue => {
  const entries = Object.entries(getDetailsRoute(root));

  return entries.reduce(
    (acc, [key, value]) => {
      return {
        ...acc,
        [key]: `${value}/edit`,
      };
    },
    { suffix: 'edit' } as EditMappedValue,
  );
};
const getListRoutesConfig = (root: string) =>
  ({
    create: getCreateRoute(root),
    edit: getEditRoute(root),
    details: getDetailsRoute(root),
  }) as const;

export const getAbsolutePath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

// please use with: https://reactrouter.com/en/main/utils/generate-path
export const ROUTES = {
  users: {
    path: '/users',
    profile: '/user-profile',
    roles: {
      path: '/users-roles',
      edit: '/users-roles/:id/:currentTab',
    },
  },
  login: 'login',
  dashboard: {
    path: '/dashboard',
  },
  stats: '/stats',
  plan: {
    path: '/plan',
    simulationCreatePath: '/plan/simulation',
    simulationListPath: '/plan/simulations-list',
    simulationPath: '/plan/simulation/:id',
    graph: '/plan/graph/:id',
  },
  workStation: {
    path: '/work-stations',
    get dashboard() {
      const tab = ':currentTab';
      return {
        tab,
        full: `${this.path}/:id/${tab}`,
        partial: `${this.path}/:id`,
        root: ':id',
      };
    },
    get initLoad() {
      return {
        partial: ':machineId/:taskId?/init-load',
        full: `${this.path}/:machineId/:taskId/init-load`,
      };
    },
    get report() {
      const tab = ':currentTab';
      return {
        tab,
        full: `${this.path}/:machineId/:taskId/report/${tab}`,
        partial: `${this.path}/:machineId/:taskId/report`,
        root: ':machineId',
        path: ':taskId/report',
      };
    },
    get batchReport() {
      const tab = ':currentTab';
      return {
        tab,
        full: `${this.path}/:machineId/:taskId/batch-report/${tab}`,
        partial: `${this.path}/:machineId/:taskId/batch-report`,
        root: ':machineId',
        path: ':taskId/batch-report',
      };
    },
  },
  contractors: {
    path: '/contractors',
    ...getListRoutesConfig('contractors'),
  },
  competenciesMatrix: {
    path: '/competencies-matrix',
  },
  tools: {
    path: '/tools',
  },
  warehouses: {
    path: '/warehouses',
  },
  traceability: {
    path: '/traceability',
    graph: '/traceability/graph/:id',
  },
  stocktakings: {
    path: '/stocktakings',
    ...getListRoutesConfig('stocktakings'),
  },
  materialMovements: {
    path: '/material-movements',
    create: 'create',
    details: {
      full: '/material-movements/:id/details',
      partial: '/:id/details',
    },
  },
  internalTransports: {
    path: '/internal-transports',
  },
  productionOrders: {
    path: '/production-orders',
    currentTab: ':currentTab?',
    //TODO NEX-434: Custom routing for production orders
    ...getListRoutesConfig('production-orders'),
  },
  productionLots: {
    path: '/production-lots',
    details: getDetailsRoute('production-lots'),
  },
  supplyOrders: {
    path: '/supply-orders',
  },
  supplyOrdersDetails: {
    full: '/supply-orders/:id',
    partial: ':id',
  },
  receptions: {
    received: {
      full: '/receptions/received',
      partial: 'received',
    },
    path: '/receptions',
    create: {
      full: '/receptions/create',
      partial: 'create',
    },
    fill: {
      full: '/receptions/:id/fill',
      partial: ':id/fill',
    },
  },
  outboundDeliveries: {
    completed: {
      full: '/outbound-deliveries/completed',
      partial: 'completed',
    },
    path: '/outbound-deliveries',
    partial: '/',
    plannedRelease: {
      full: '/outbound-deliveries/release/:id',
      partial: 'release/:id',
    },
  },
  synchronizations: {
    path: '/synchronizations',
    import: '/import',
    export: '/export',
  },
  products: {
    path: '/products',
    ...getListRoutesConfig('products'),
  },
  orders: {
    path: '/orders',
    ...getListRoutesConfig('orders'),
  },
  offers: {
    path: '/offers',
    ...getListRoutesConfig('offers'),
  },
  workers: {
    path: '/workers',
    ...getListRoutesConfig('workers'),
  },
  workersSettings: {
    path: '/workers-settings',
    ...getMainPageTabsRoute('workers-settings'),
  },
  workstationSettings: {
    path: '/workstation-settings',
    ...getListRoutesConfig('workstation-settings'),
  },
  productsTechnologySchemas: {
    path: '/technology-schemas/products',
    details: getDetailsRoute('technology-schemas/products'),
  },
  productGroupsTechnologySchemas: {
    path: '/technology-schemas/product-groups',
  },
  retoolingMatrix: {
    path: '/retooling-matrix',
  },
} as const;
